/** TanStack Query hooks for share link CRUD and document publishing, plus pure URL helpers */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'

export interface ShareLink {
  id: string
  token: string
  accessLevel: string
  expiresAt: string | null
  createdAt: string
}

interface CreateShareLinkResponse {
  id: string
  token: string
  url: string
}

interface PublishDocumentResponse {
  slug: string
  url: string
  publishedAt: string
}

/** Unpublish a document; the API restores the access level it had before it was published */
export function useUnpublishDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) =>
      apiClient.post<{ accessLevel: string }>(`/api/share/unpublish/${encodeURIComponent(documentId)}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  })
}

/** Absolute URL of the public share view for a token */
export function shareLinkUrl(token: string, origin: string): string {
  return `${origin}/share/${encodeURIComponent(token)}`
}

/** Absolute URL of the published (public) view for a document slug */
export function publishedDocUrl(slug: string, origin: string): string {
  return `${origin}/pub/${encodeURIComponent(slug)}`
}

/** A link without an expiry never expires; an unparseable date counts as expired so it is never shown as live */
export function isShareLinkExpired(expiresAt: string | null, now: number = Date.now()): boolean {
  if (!expiresAt) return false
  const time = new Date(expiresAt).getTime()
  return Number.isNaN(time) || time <= now
}

export function useShareLinks(documentId: string | null) {
  return useQuery<{ links: ShareLink[] }>({
    queryKey: ['share-links', documentId],
    queryFn: () => apiClient.get<{ links: ShareLink[] }>(`/api/share/links/${documentId}`),
    enabled: !!documentId,
  })
}

export function useCreateShareLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { documentId: string; expiresInDays?: number }) =>
      apiClient.post<CreateShareLinkResponse>('/api/share/links', body),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['share-links', variables.documentId] })
    },
  })
}

export function useDeleteShareLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { id: string; documentId: string }) =>
      apiClient.delete<{ ok: boolean }>(`/api/share/links/${params.id}`),
    onSuccess: (_, params) => {
      qc.invalidateQueries({ queryKey: ['share-links', params.documentId] })
    },
  })
}

/** POST /api/share/publish/:id — renders the page and sets access to public. The response is not a document, so only invalidate. */
export function usePublishDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, description }: { documentId: string; description: string }) =>
      apiClient.post<PublishDocumentResponse>(`/api/share/publish/${documentId}`, { description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  })
}
